import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiCheckCircle, FiXCircle, FiClock, FiAlertCircle, FiFilter, FiCheck, FiAlertTriangle, FiDownload, FiEye, FiThumbsUp, FiThumbsDown, FiUser, FiLoader } from 'react-icons/fi';
import { actionsAPI, documentsAPI, foldersAPI, sharepointAPI } from '../lib/api';
import useAuthStore from '../store/authStore';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useToast } from '../components/ToastContainer';

const MeToAction = () => {
  const { profile } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useToast();

  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [sortBy, setSortBy] = useState('created');

  // SharePoint download choice modal state
  const [showSharePointDownloadModal, setShowSharePointDownloadModal] = useState(false);
  const [sharePointDocForDownload, setSharePointDocForDownload] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState('');

  // Initialize filters from URL parameters
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const priorityParam = searchParams.get('priority');
    
    if (statusParam) {
      setFilterStatus(statusParam);
    }
    if (priorityParam) {
      setFilterPriority(priorityParam);
    }
  }, [searchParams]);
  const [selectedActions, setSelectedActions] = useState([]);
  const [responseModal, setResponseModal] = useState({ show: false, action: null });
  const [bulkResponseModal, setBulkResponseModal] = useState({ show: false, status: '' });
  const [responseNotes, setResponseNotes] = useState('');
  const [pendingAction, setPendingAction] = useState(null); // Track which button was clicked
  const previousActionsCountRef = useRef(null);
  const previousStatsRef = useRef(null);

  // Fetch my actions with auto-refresh every 30 seconds
  const { data: actions, isLoading } = useQuery({
    queryKey: ['my-actions', filterStatus, filterType, filterPriority],
    queryFn: () => actionsAPI.getMyActions({
      status: filterStatus || undefined,
      action_type: filterType || undefined,
      priority: filterPriority || undefined,
    }),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch stats with auto-refresh every 30 seconds
  const { data: stats } = useQuery({
    queryKey: ['my-action-stats'],
    queryFn: () => actionsAPI.getMyStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch folders for SharePoint download modal
  const { data: foldersData } = useQuery({
    queryKey: ['folders'],
    queryFn: foldersAPI.getAll,
  });

  // Detect changes in actions list and show notifications
  useEffect(() => {
    if (actions?.data && previousActionsCountRef.current !== null) {
      const currentCount = actions.data.length;
      const prevCount = previousActionsCountRef.current;

      if (currentCount > prevCount) {
        const diff = currentCount - prevCount;
        showInfo(`${diff} new action${diff > 1 ? 's' : ''} added!`, 5000);
      }
    }

    // Update the reference with current count
    if (actions?.data) {
      previousActionsCountRef.current = actions.data.length;
    }
  }, [actions, showInfo]);

  // Detect changes in stats and show notifications
  useEffect(() => {
    if (stats?.data && previousStatsRef.current) {
      const prev = previousStatsRef.current;
      const curr = stats.data;

      // Check for new pending actions (only if on pending view or no filter)
      if (curr.pending > prev.pending && (!filterStatus || filterStatus === 'pending')) {
        const diff = curr.pending - prev.pending;
        showInfo(`You have ${diff} new action${diff > 1 ? 's' : ''} to review!`, 5000);
      }
    }

    // Update the reference with current stats
    if (stats?.data) {
      previousStatsRef.current = stats.data;
    }
  }, [stats, filterStatus, showInfo]);

  // Respond mutation
  const respondMutation = useMutation({
    mutationFn: ({ id, status, response_notes }) =>
      actionsAPI.respond(id, { status, response_notes }),
    onMutate: (variables) => {
      setPendingAction(variables.status);
      showInfo('Sending email notification...', 10000);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['my-actions']);
      queryClient.invalidateQueries(['my-action-stats']);
      setResponseModal({ show: false, action: null });
      setResponseNotes('');
      setPendingAction(null);
      const statusLabels = {
        approved: 'approved',
        rejected: 'rejected',
        completed: 'marked as complete'
      };
      showSuccess(`Action ${statusLabels[variables.status]} and email notification sent!`);
    },
    onError: (error) => {
      setPendingAction(null);
      showError(error.response?.data?.error || 'Failed to process action');
    },
  });

  // Bulk respond mutation
  const bulkRespondMutation = useMutation({
    mutationFn: ({ action_ids, status, response_notes }) =>
      actionsAPI.bulkRespond({ action_ids, status, response_notes }),
    onMutate: () => {
      showInfo('Processing bulk actions...', 10000);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['my-actions']);
      queryClient.invalidateQueries(['my-action-stats']);
      setSelectedActions([]);
      setBulkResponseModal({ show: false, status: '' });
      setResponseNotes('');
      showSuccess(`${variables.action_ids.length} action(s) processed successfully!`);
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to process bulk actions');
    },
  });

  const handleBulkResponse = (status) => {
    if (selectedActions.length === 0) return;
    setBulkResponseModal({ show: true, status });
  };

  const confirmBulkResponse = () => {
    bulkRespondMutation.mutate({
      action_ids: selectedActions,
      status: bulkResponseModal.status,
      response_notes: responseNotes,
    });
  };

  // Sort actions based on sortBy state
  const sortedActions = useMemo(() => {
    const actionsList = actions?.data?.actions || [];
    if (actionsList.length === 0) return [];

    return [...actionsList].sort((a, b) => {
      if (sortBy === 'due_date') {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      } else if (sortBy === 'priority') {
        const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      } else {
        // Default: created - newest first
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });
  }, [actions?.data?.actions, sortBy]);

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      normal: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800',
    };
    return colors[priority] || colors.normal;
  };

  const getActionTypeLabel = (type) => {
    const labels = {
      approval: 'Approval Required',
      review: 'Review',
      signature: 'Signature',
      acknowledgment: 'Acknowledgment',
      feedback: 'Feedback',
    };
    return labels[type] || type;
  };

  const toggleSelectAll = () => {
    if (selectedActions.length === actions?.data?.actions?.length) {
      setSelectedActions([]);
    } else {
      setSelectedActions(actions?.data?.actions?.map(a => a.id) || []);
    }
  };

  const toggleSelect = (id) => {
    setSelectedActions(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleDownload = async (documentId, documentName) => {
    try {
      const response = await documentsAPI.download(documentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', documentName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleView = (documentId) => {
    window.open(`/documents/${documentId}`, '_blank');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Me To Action</h1>
        <p className="text-gray-600 mt-1">Manage documents assigned to you for action</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-blue-600">{stats?.data?.pending || 0}</p>
            </div>
            <FiClock className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Urgent</p>
              <p className="text-2xl font-bold text-orange-600">{stats?.data?.byPriority?.urgent || 0}</p>
            </div>
            <FiAlertTriangle className="w-8 h-8 text-orange-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{stats?.data?.overdue || 0}</p>
            </div>
            <FiAlertCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-emerald-600">{stats?.data?.approved || 0}</p>
            </div>
            <FiThumbsUp className="w-8 h-8 text-emerald-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-rose-600">{stats?.data?.rejected || 0}</p>
            </div>
            <FiThumbsDown className="w-8 h-8 text-rose-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Assign To Me</p>
              <p className="text-2xl font-bold text-indigo-600">{stats?.data?.assigned_to_me || 0}</p>
            </div>
            <FiUser className="w-8 h-8 text-indigo-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats?.data?.completed || 0}</p>
            </div>
            <FiCheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Filters and Bulk Actions */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-4 items-center">
            <FiFilter className="w-5 h-5 text-gray-500" />

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All (My Actions)</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="completed">Completed</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All Types</option>
              <option value="approval">Approval</option>
              <option value="review">Review</option>
              <option value="signature">Signature</option>
              <option value="acknowledgment">Acknowledgment</option>
              <option value="feedback">Feedback</option>
            </select>

            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="created">Newest First</option>
              <option value="due_date">Due Date</option>
              <option value="priority">Priority</option>
            </select>
          </div>

          {selectedActions.length > 0 && (
            <div className="flex gap-2">
              {filterStatus === 'pending' && (
                <>
                  <button
                    onClick={() => handleBulkResponse('approved')}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <FiCheckCircle /> Approve ({selectedActions.length})
                  </button>
                  <button
                    onClick={() => handleBulkResponse('rejected')}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                  >
                    <FiXCircle /> Reject ({selectedActions.length})
                  </button>
                  <button
                    onClick={() => handleBulkResponse('completed')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <FiCheck /> Complete ({selectedActions.length})
                  </button>
                </>
              )}
              {(filterStatus === 'approved' || filterStatus === 'rejected') && (
                <button
                  onClick={() => handleBulkResponse('completed')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <FiCheck /> Mark Complete ({selectedActions.length})
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {(filterStatus === 'pending' || filterStatus === 'approved' || filterStatus === 'rejected') && (
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedActions.length === actions?.data?.actions?.length && actions?.data?.actions?.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                  Loading actions...
                </td>
              </tr>
            ) : sortedActions.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                  No actions found
                </td>
              </tr>
            ) : (
              sortedActions.map((action) => (
                <tr key={action.id} className="hover:bg-gray-50">
                  {(filterStatus === 'pending' || filterStatus === 'approved' || filterStatus === 'rejected') && (
                    <td className="px-6 py-4">
                      {(filterStatus === 'pending' || ((filterStatus === 'approved' || filterStatus === 'rejected') && action.is_assigner)) && (
                        <input
                          type="checkbox"
                          checked={selectedActions.includes(action.id)}
                          onChange={() => toggleSelect(action.id)}
                          className="rounded"
                        />
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900 mb-1">{action.document?.name}</div>
                    <div className="text-sm text-gray-500 mb-2">{action.document?.folder?.name || 'Root'}</div>
                    {action.document?.is_sharepoint_reference && (
                      <div className="text-xs text-blue-600 mb-2">SharePoint File</div>
                    )}
                    <div className="flex items-center gap-2">
                      {/* Only show View for non-SharePoint documents */}
                      {!action.document?.is_sharepoint_reference && (
                        <button
                          onClick={() => handleView(action.document?.id)}
                          className="text-primary-600 hover:text-primary-900 flex items-center gap-1 text-xs"
                          title="View document"
                        >
                          <FiEye className="w-3.5 h-3.5" />
                          <span>View</span>
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          // Handle SharePoint documents - show choice modal
                          if (action.document?.is_sharepoint_reference) {
                            setSharePointDocForDownload({
                              ...action.document?.sharepoint_metadata,
                              name: action.document?.name,
                              id: action.document?.sharepoint_metadata?.itemId,
                            });
                            setShowSharePointDownloadModal(true);
                            return;
                          }

                          // Regular document download
                          handleDownload(action.document?.id, action.document?.name);
                        }}
                        className="text-primary-600 hover:text-primary-900 flex items-center gap-1 text-xs"
                        title="Download document"
                      >
                        <FiDownload className="w-3.5 h-3.5" />
                        <span>Download</span>
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm">{getActionTypeLabel(action.action_type)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(action.priority)}`}>
                      {action.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      action.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      action.status === 'approved' ? 'bg-green-100 text-green-800' :
                      action.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {action.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {action.is_assigner ? (
                        <>
                          <div className="font-medium">{action.assignee?.full_name || action.assignee?.email}</div>
                          <div className="text-xs text-gray-500">Assigned To</div>
                        </>
                      ) : (
                        <>
                          <div className="font-medium">{action.assigner?.full_name || action.assigner?.email}</div>
                          <div className="text-xs text-gray-500">Assigned By</div>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {action.due_date ? new Date(action.due_date).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {action.notes || '-'}
                    {action.response_notes && (
                      <div className="mt-1 text-xs italic text-gray-600">
                        Response: {action.response_notes}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium">
                    {/* Show "Respond" for assignees on pending actions */}
                    {(action.status === 'pending' && !action.is_assigner) && (
                      <button
                        onClick={() => setResponseModal({ show: true, action })}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Respond
                      </button>
                    )}
                    {/* Show "Mark Complete" for assigners on approved/rejected actions */}
                    {(action.is_assigner && (action.status === 'approved' || action.status === 'rejected')) && (
                      <button
                        onClick={() => setResponseModal({ show: true, action })}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Mark Complete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Response Modal */}
      {responseModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">
              {responseModal.action?.is_assigner ? 'Mark Action as Complete' : 'Respond to Action'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Document: <span className="font-medium">{responseModal.action?.document?.name}</span>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Type: <span className="font-medium">{getActionTypeLabel(responseModal.action?.action_type)}</span>
            </p>
            {responseModal.action?.is_assigner && (
              <p className="text-sm text-gray-600 mb-4">
                Status: <span className={`px-2 py-1 text-xs rounded-full ${
                  responseModal.action?.status === 'approved' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {responseModal.action?.status}
                </span>
              </p>
            )}
            {responseModal.action?.notes && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Request Notes:</label>
                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{responseModal.action.notes}</p>
              </div>
            )}
            {responseModal.action?.response_notes && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Response from {responseModal.action.assignee?.full_name}:</label>
                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{responseModal.action.response_notes}</p>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {responseModal.action?.is_assigner ? 'Completion Notes:' : 'Response Notes:'}
              </label>
              <textarea
                value={responseNotes}
                onChange={(e) => setResponseNotes(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows="3"
                placeholder={responseModal.action?.is_assigner ? 'Enter completion notes...' : 'Enter your response notes...'}
              />
            </div>
            <div className="flex gap-2">
              {responseModal.action?.is_assigner ? (
                // Assigner can only mark as complete
                <button
                  onClick={() => {
                    respondMutation.mutate({
                      id: responseModal.action.id,
                      status: 'completed',
                      response_notes: responseNotes,
                    });
                  }}
                  disabled={respondMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {pendingAction === 'completed' ? (
                    <>
                      <FiLoader className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
              ) : (
                // Assignee can approve, reject, or complete
                <>
                  <button
                    onClick={() => {
                      respondMutation.mutate({
                        id: responseModal.action.id,
                        status: 'approved',
                        response_notes: responseNotes,
                      });
                    }}
                    disabled={respondMutation.isPending}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {pendingAction === 'approved' ? (
                      <>
                        <FiLoader className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Approve'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      respondMutation.mutate({
                        id: responseModal.action.id,
                        status: 'rejected',
                        response_notes: responseNotes,
                      });
                    }}
                    disabled={respondMutation.isPending}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {pendingAction === 'rejected' ? (
                      <>
                        <FiLoader className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Reject'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      respondMutation.mutate({
                        id: responseModal.action.id,
                        status: 'completed',
                        response_notes: responseNotes,
                      });
                    }}
                    disabled={respondMutation.isPending}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {pendingAction === 'completed' ? (
                      <>
                        <FiLoader className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Complete'
                    )}
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setResponseModal({ show: false, action: null });
                  setResponseNotes('');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Response Modal */}
      {bulkResponseModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Bulk {bulkResponseModal.status}</h3>
            <p className="text-sm text-gray-600 mb-4">
              You are about to {bulkResponseModal.status} {selectedActions.length} action(s).
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Response Notes (Optional):</label>
              <textarea
                value={responseNotes}
                onChange={(e) => setResponseNotes(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows="3"
                placeholder="Enter your response notes..."
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={confirmBulkResponse}
                disabled={bulkRespondMutation.isPending}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                Confirm
              </button>
              <button
                onClick={() => {
                  setBulkResponseModal({ show: false, status: '' });
                  setResponseNotes('');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Download Choice Modal */}
      {showSharePointDownloadModal && sharePointDocForDownload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Download Options</h2>
              <p className="text-sm text-gray-600 mb-6">
                How would you like to access <span className="font-medium">{sharePointDocForDownload.name}</span>?
              </p>

              <div className="space-y-4">
                {/* Option 1: View in SharePoint */}
                <button
                  onClick={() => {
                    if (sharePointDocForDownload.webUrl) {
                      window.open(sharePointDocForDownload.webUrl, '_blank');
                    }
                    setShowSharePointDownloadModal(false);
                    setSharePointDocForDownload(null);
                    setSelectedFolderId('');
                  }}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <FiEye className="w-5 h-5" />
                  View in SharePoint
                </button>

                {/* Folder Selection for Upload */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Folder for Upload <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedFolderId}
                    onChange={(e) => setSelectedFolderId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  >
                    <option value="">Select a folder...</option>
                    {foldersData?.data?.folders
                      ?.sort((a, b) => (a.folder_path || '').localeCompare(b.folder_path || ''))
                      .map((folder) => {
                        const depth = (folder.folder_path?.split('/').filter(Boolean).length || 1) - 1;
                        const prefix = '  '.repeat(depth) + (depth > 0 ? '└─ ' : '');
                        return (
                          <option key={folder.id} value={folder.id}>
                            {prefix}{folder.name}
                          </option>
                        );
                      })}
                  </select>
                </div>

                {/* Option 2: Upload to Doxolve */}
                <button
                  onClick={async () => {
                    if (!selectedFolderId) {
                      showError('Please select a folder first');
                      return;
                    }
                    setShowSharePointDownloadModal(false);
                    showInfo('Downloading and processing SharePoint file...', 5000);
                    try {
                      const response = await sharepointAPI.processFile({
                        connectionId: sharePointDocForDownload.connectionId,
                        driveId: sharePointDocForDownload.driveId,
                        itemId: sharePointDocForDownload.id,
                        fileName: sharePointDocForDownload.name,
                        siteId: sharePointDocForDownload.siteId,
                        siteName: sharePointDocForDownload.tenantName,
                        driveName: sharePointDocForDownload.driveName || 'Documents',
                        folderId: selectedFolderId
                      });

                      showSuccess('SharePoint file uploaded to Doxolve successfully!');
                      queryClient.invalidateQueries(['my-actions']);

                      if (response.data.documentId) {
                        navigate(`/documents/${response.data.documentId}`);
                      }
                    } catch (error) {
                      console.error('Process error:', error);
                      showError(error.response?.data?.error || 'Failed to upload to Doxolve');
                    } finally {
                      setSharePointDocForDownload(null);
                      setSelectedFolderId('');
                    }
                  }}
                  disabled={!selectedFolderId}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiDownload className="w-5 h-5" />
                  Upload to Doxolve
                </button>

                {/* Cancel Button */}
                <button
                  onClick={() => {
                    setShowSharePointDownloadModal(false);
                    setSharePointDocForDownload(null);
                    setSelectedFolderId('');
                  }}
                  className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeToAction;
